import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { HxButton } from '../button/button';
import {
  COLUMN_OPTIONS_EDITOR_OPERATORS,
  type OperatorMetadata,
  type RuleActionType,
  type RuleOperator,
  type UnifiedRule,
} from '../column-options-editor/column-options-editor.types';
import { HxDialogRef } from '../dialog/dialog-ref';
import { HxDialogComponent } from '../dialog/dialog.component';
import { DIALOG_DATA, DIALOG_REF } from '../dialog/internal/dialog-injectors';
import { HxIconButton } from '../icon-button/icon-button';
import { HxDropdownComponent } from '../select/dropdown/dropdown.component';
import {
  normalizeBasicControlRules,
  validateBasicControlRules,
  type BasicControlRulesEditorConfig,
} from './basic-control-rules-editor.types';

@Component({
  selector: 'hx-basic-control-rules-editor',
  standalone: true,
  imports: [CommonModule, HxButton, HxDialogComponent, HxIconButton, HxDropdownComponent],
  templateUrl: './basic-control-rules-editor.html',
  styleUrl: './basic-control-rules-editor.css',
  encapsulation: ViewEncapsulation.None,
})
export class HxBasicControlRulesEditorDialogComponent implements OnInit {
  private readonly dialogRef =
    inject<HxDialogRef<HxBasicControlRulesEditorDialogComponent, BasicControlRulesEditorConfig>>(DIALOG_REF);
  protected readonly data = inject<BasicControlRulesEditorConfig>(DIALOG_DATA);

  readonly rulesState = signal<UnifiedRule[]>([]);
  readonly validationErrors = signal<string[]>([]);
  readonly showValidationErrors = signal(false);
  readonly hasRules = computed(() => this.rulesState().length > 0);

  readonly dialogTitle = computed(() => this.data.title?.trim() || `Set Rules: ${this.data.controlLabel}`);
  readonly optionChoices = computed(() =>
    this.data.options.map((option) => ({ id: option.value, value: option.value, label: option.label }))
  );
  readonly actionTypeOptions = computed(() => [
    { id: 'hide', value: 'hide' as RuleActionType, label: 'Hide value(s)' },
    { id: 'require', value: 'require' as RuleActionType, label: 'Require value(s)' },
    { id: 'disable', value: 'disable' as RuleActionType, label: 'Disable value(s)' },
  ]);

  ngOnInit(): void {
    this.rulesState.set(
      normalizeBasicControlRules(
        this.data.rules,
        this.data.options.map((option) => option.value)
      )
    );
  }

  getOperatorOptions(): { id: string; value: RuleOperator; label: string }[] {
    const isSingleValue = this.data.inputType === 'single-select';
    return COLUMN_OPTIONS_EDITOR_OPERATORS.filter(
      (operator: OperatorMetadata) => isSingleValue === !operator.supportsMultipleValues
    ).map((operator) => ({
      id: operator.value,
      value: operator.value,
      label: operator.label,
    }));
  }

  addRule(): void {
    // Set default operator based on control type: 'equals' for single-select, 'includes' for multi-select
    const defaultOperator = (this.getOperatorOptions()[0]?.value ?? 'equals') as RuleOperator;

    const newRule: UnifiedRule = {
      id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      when: { column: '', operator: defaultOperator, values: [] },
      then: { type: 'hide', targetColumn: '', targetValues: [] },
      enabled: true,
    };

    this.rulesState.update((rules) => [...rules, ...normalizeBasicControlRules([newRule])]);
  }

  removeRule(id: string): void {
    this.rulesState.update((rules) => rules.filter((rule) => rule.id !== id));
    this.showValidationErrors.set(false);
  }

  toggleRuleEnabled(id: string): void {
    this.rulesState.update((rules) =>
      rules.map((rule) => (rule.id === id ? { ...rule, enabled: !rule.enabled } : rule))
    );
  }

  updateRuleConditionOperator(id: string, operator: RuleOperator): void {
    this.rulesState.update((rules) =>
      rules.map((rule) => (rule.id === id ? { ...rule, when: { ...rule.when, operator, values: [] } } : rule))
    );
  }

  updateRuleConditionValues(id: string, values: string | string[]): void {
    // hx-dropdown emits a bare value (not an array) when [multiple] is false (single-select condition)
    const normalizedValues = Array.isArray(values) ? values : values == null || values === '' ? [] : [values];
    this.rulesState.update((rules) =>
      rules.map((rule) => (rule.id === id ? { ...rule, when: { ...rule.when, values: normalizedValues } } : rule))
    );
  }

  updateRuleActionType(id: string, type: RuleActionType): void {
    this.rulesState.update((rules) =>
      rules.map((rule) => (rule.id === id ? { ...rule, then: { ...rule.then, type, targetValues: [] } } : rule))
    );
  }

  updateRuleActionValues(id: string, targetValues: string[]): void {
    this.rulesState.update((rules) =>
      rules.map((rule) => (rule.id === id ? { ...rule, then: { ...rule.then, targetValues } } : rule))
    );
  }

  dismissValidationErrors(): void {
    this.showValidationErrors.set(false);
  }

  save(): void {
    const result = this.buildResult();
    const validation = validateBasicControlRules(
      result.rules,
      this.data.options.map((option) => option.value)
    );

    if (!validation.valid) {
      this.validationErrors.set(validation.errors);
      this.showValidationErrors.set(true);
      return;
    }

    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildResult(): BasicControlRulesEditorConfig {
    return {
      ...this.data,
      rules: normalizeBasicControlRules(
        this.rulesState().map((rule) => ({ ...rule, when: { ...rule.when }, then: { ...rule.then } })),
        this.data.options.map((option) => option.value)
      ),
    };
  }
}
